
import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AuthError, AuthApiError } from "@supabase/supabase-js";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { AlertTitle } from "@/components/ui/alert";
import { ArrowLeft } from "lucide-react";
import RequestInviteForm from "@/components/invites/RequestInviteForm";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  useEffect(() => {
    // Check for invite code in URL params
    const inviteParam = searchParams.get('invite');
    const resetParam = searchParams.get('reset');
    const registerParam = searchParams.get('register');
    
    if (inviteParam) {
      setInviteCode(inviteParam);
      setIsRegister(true);
      toast({
        title: "Invitation received!",
        description: `Use code ${inviteParam} to register`,
      });
    }

    if (registerParam === 'true') {
      setIsRegister(true); // Switch to register mode
    }

    if (resetParam === 'true') {
      setIsRegister(false); // Switch to sign in mode for password reset
    }

    const handleEmailConfirmation = async () => {
      const hash = location.hash;
      if (hash) {
        const params = new URLSearchParams(hash.substring(1));
        const error = params.get("error");
        const errorDescription = params.get("error_description");

        if (error === "access_denied" && params.get("error_code") === "otp_expired") {
          toast({
            title: "Link Expired",
            description: "Your verification link has expired. Please sign up again to receive a new link.",
            variant: "destructive",
          });
          setIsRegister(true);
          return;
        }

        if (hash.includes("access_token")) {
          try {
            const { data: { session }, error } = await supabase.auth.getSession();
            if (error) throw error;
            if (session) {
              // After successful auth, use invite code if present
              if (inviteParam) {
                await supabase.rpc('use_invite_code', {
                  invite_code_param: inviteParam,
                  user_id_param: session.user.id
                });
              }
              
              toast({
                title: "Email verified! 🎉",
                description: "Complete your Positivio Club membership now.",
              });
              navigate("/?join=true");
            }
          } catch (error: any) {
            setErrorMessage(getErrorMessage(error));
          }
        }
      }
    };

    handleEmailConfirmation();
  }, [location, navigate, toast, searchParams]);

  const getErrorMessage = (error: AuthError) => {
    if (error instanceof AuthApiError) {
      switch (error.code) {
        case 'invalid_credentials':
          return 'Invalid email or password. Please check your credentials and try again.';
        case 'email_not_confirmed':
          return 'Please verify your email address before signing in.';
        case 'user_not_found':
          return 'No user found with these credentials.';
        case 'invalid_grant':
          return 'Invalid login credentials.';
        case 'user_already_exists':
          return 'An account with this email already exists. Please sign in instead.';
        default:
          if (error.message.includes("User already registered")) {
            return 'An account with this email already exists. Please sign in instead.';
          }
          return error.message;
      }
    }
    return error.message;
  };

  const handleResendConfirmation = async () => {
    if (resendTimer > 0) return;
    
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      
      if (error) throw error;
      
      toast({
        title: "Confirmation email resent! 📧",
        description: "Please check your inbox and spam folder.",
        duration: 6000,
      });
      
      setResendTimer(60);
      const timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });

      if (error) throw error;

      setSuccessMessage("Password reset email sent! Please check your inbox.");
      setShowForgotPassword(false);
      setResetEmail("");
      toast({
        title: "Reset email sent! 📧",
        description: "Check your inbox for password reset instructions.",
        duration: 6000,
      });
    } catch (error: any) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      if (isRegister) {
        if (password !== confirmPassword) {
          throw new Error("Passwords do not match");
        }

        if (!inviteCode.trim()) {
          throw new Error("Invite code is required");
        }

        // First validate the invite code
        const { data: inviteValid } = await supabase.rpc('validate_invite_code', {
          code: inviteCode.trim()
        });

        if (!inviteValid) {
          throw new Error("Invalid or expired invite code");
        }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              username: name,
              invite_code: inviteCode.trim(),
            },
            emailRedirectTo: `${window.location.origin}/auth?invite=${inviteCode.trim()}`,
          },
        });

        if (error) throw error;

        // Check specifically for user already registered error
        if (data.user && data.user.identities && data.user.identities.length === 0) {
          // This is a sign that the user already exists
          setErrorMessage('An account with this email already exists. Please sign in instead.');
          setIsRegister(false);
          setIsLoading(false);
          return;
        }

        if (data.user && !data.session) {
          setSuccessMessage("Account created! Check your email to verify and complete your Positivio Club membership.");
          toast({
            title: "Welcome to Positivio Club! 🎉",
            description: "We've sent you a confirmation email. Please verify to complete your membership.",
            duration: 8000,
          });
          setResendTimer(0);
        } else {
          toast({
            title: "Welcome to Positivio Club! 🎉",
            description: "Please complete your membership to join the club.",
          });
          navigate("/?join=true");
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        navigate("/");
      }
    } catch (error: any) {
      setErrorMessage(getErrorMessage(error));
      
      // If the user already exists, prompt them to sign in instead
      if (error.message.includes("User already registered")) {
        setIsRegister(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (showRequestForm) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <img 
              src="/lovable-uploads/e9ee540f-ae0a-4d24-b1a2-69eb3153196d.png" 
              alt="Positivio" 
              className="h-24 mx-auto mb-2"
            />
            <h2 className="text-2xl font-bold">Join the Waitlist</h2>
            <p className="text-muted-foreground">
              Get your exclusive invite to Positivio
            </p>
          </div>
          
          <RequestInviteForm />
          
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowRequestForm(false)}
              className="text-sm text-muted-foreground hover:text-primary"
            >
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-pink-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Back Button */}
        <div className="flex justify-start mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Button>
        </div>

        <div className="text-center">
          <img 
            src="/lovable-uploads/e9ee540f-ae0a-4d24-b1a2-69eb3153196d.png" 
            alt="Positivio" 
            className="h-24 mx-auto mb-2"
          />
          <h2 className="text-2xl font-bold">{isRegister ? "Create an account" : "Welcome back"}</h2>
          <p className="text-muted-foreground">
            {isRegister ? "Join our positive community" : "Sign in to spread positivity"}
          </p>
        </div>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        {successMessage && (
          <Alert variant="default" className="border-green-500 bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300">
            <AlertTitle>Success!</AlertTitle>
            <AlertDescription>{successMessage}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleAuth} className="space-y-6">
          {isRegister && (
            <>
              <div className="space-y-2">
                <Label htmlFor="inviteCode">Invite code *</Label>
                <Input
                  id="inviteCode"
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  required={isRegister}
                  placeholder="ABC12345"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  Access by invitation only. 
                  <button
                    type="button"
                    onClick={() => setShowRequestForm(true)}
                    className="text-primary hover:underline ml-1"
                  >
                    Request an invite here
                  </button>
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Username</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={isRegister}
                  placeholder="johndoe"
                />
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="john@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
            />
          </div>
          {isRegister && (
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required={isRegister}
                placeholder="••••••••"
              />
            </div>
          )}
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : isRegister ? "Sign Up" : "Sign In"}
          </Button>
          
          {isRegister && email && (
            <Button
              type="button"
              variant="outline"
              className="w-full mt-2"
              onClick={handleResendConfirmation}
              disabled={resendTimer > 0 || isLoading}
            >
              {resendTimer > 0 
                ? `Resend in ${resendTimer}s` 
                : "Resend confirmation email"}
            </Button>
          )}
          
          {!isRegister && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-muted-foreground hover:text-primary underline"
              >
                Forgot your password?
              </button>
            </div>
          )}

        </form>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-background border rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Reset Password</h3>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resetEmail">Email</Label>
                  <Input
                    id="resetEmail"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    placeholder="Enter your email address"
                  />
                  <p className="text-xs text-muted-foreground">
                    We'll send you a link to reset your password.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {isLoading ? "Sending..." : "Send Reset Link"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              setIsRegister(!isRegister);
              setErrorMessage("");
              setSuccessMessage("");
              setShowForgotPassword(false);
            }}
            className="text-sm text-muted-foreground hover:text-primary"
          >
            {isRegister
              ? "Already have an account? Sign in"
              : "Don't have an account? Sign up"}
          </button>
          
          {!isRegister && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowRequestForm(true)}
                className="text-sm text-primary hover:underline"
              >
                Need an invite code? Request one here
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
